import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { EdiphiDatasourceType, TakeOffSourceType } from "./DatasourceContext";
import { ReportGroup } from "../../excel/reports/api/groups";

export type DrawerId = "ediphi" | "takeOff" | "reports" | "insights";
export type DrawerMode = "add" | "edit";

interface DrawerSelectionMap {
  ediphi: EdiphiDatasourceType;
  takeOff: TakeOffSourceType;
  reports: ReportGroup;
  /** Charts are added from one flat list, so the drawer carries no selection. */
  insights: never;
}

interface DrawerState {
  id: DrawerId;
  mode: DrawerMode;
  selection?: DrawerSelectionMap[DrawerId];
  isOpen: boolean;
}

interface DrawerContextType {
  state: DrawerState | null;
  openDrawer: <K extends DrawerId>(id: K, mode: DrawerMode, selection?: DrawerSelectionMap[K]) => void;
  closeDrawer: () => void;
  setSelection: <K extends DrawerId>(id: K, selection: DrawerSelectionMap[K]) => void;
}

const DrawerContext = createContext<DrawerContextType | undefined>(undefined);

export const DrawerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<DrawerState | null>(null);

  const openDrawer = useCallback(<K extends DrawerId>(id: K, mode: DrawerMode, selection?: DrawerSelectionMap[K]) => {
    setState({ id, mode, selection, isOpen: true });
  }, []);

  /**
   * Flips `isOpen` and nothing else. The drawer stays mounted so MUI can run its exit
   * transition, and it reads `mode` and `selection` the whole way out — clearing either here
   * would visibly change the drawer's title and contents mid-slide.
   */
  const closeDrawer = useCallback(() => {
    setState((current) => (current ? { ...current, isOpen: false } : current));
  }, []);

  const setSelection = useCallback(<K extends DrawerId>(id: K, selection: DrawerSelectionMap[K]) => {
    setState((current) => (current && current.id === id ? { ...current, selection } : current));
  }, []);

  const value = useMemo(
    () => ({ state, openDrawer, closeDrawer, setSelection }),
    [state, openDrawer, closeDrawer, setSelection]
  );

  return <DrawerContext.Provider value={value}>{children}</DrawerContext.Provider>;
};

const useDrawerContext = (): DrawerContextType => {
  const context = useContext(DrawerContext);
  if (!context) {
    throw new Error("useDrawer / useDrawerActions must be used within a DrawerProvider");
  }
  return context;
};

/** Open/close from any depth, without threading a callback down the tree. */
export const useDrawerActions = () => {
  const { openDrawer, closeDrawer } = useDrawerContext();
  return { openDrawer, closeDrawer };
};

/** A single drawer's own view of the state, typed to its selection. */
export const useDrawer = <K extends DrawerId>(id: K) => {
  const { state, closeDrawer, setSelection } = useDrawerContext();

  const select = useCallback((selection: DrawerSelectionMap[K]) => setSelection(id, selection), [setSelection, id]);

  return {
    isOpen: state?.id === id && state.isOpen,
    // Defaulted so a drawer that has never been opened still renders its add-mode chrome
    // rather than needing an undefined check at every read.
    mode: state?.id === id ? state.mode : "add",
    selection: state?.id === id ? (state.selection as DrawerSelectionMap[K] | undefined) : undefined,
    select,
    close: closeDrawer,
  };
};
